import type { Vec2 } from '../core/math';
import { weaponPoint, type WeaponAnchor } from '../sim/weapons/geometry';
import { PRIM_CAPSULE, type Primitive } from './sdf/primitives';

/** Palm contacts in the weapon's local frame, below the receiver at the actual handle. */
export function weaponHandholds(kind: string, length: number) {
  // Equipment is grasped by its actual edge, seat or handle instead of a gun's pistol grip.
  switch (kind) {
    case 'return-disc':
      return { main: { x: 0, y: 0 }, support: { x: 0, y: -0.12 }, supportUnderBarrel: false };
    case 'office-stapler':
      return { main: { x: 0, y: -0.055 }, support: { x: 0.2, y: -0.055 }, supportUnderBarrel: false };
    case 'office-chair':
      return { main: { x: -0.14, y: 0.23 }, support: { x: 0.18, y: -0.02 }, supportUnderBarrel: false };
    case 'office-monitor':
      return { main: { x: -0.2, y: 0.02 }, support: { x: 0.36, y: 0.02 }, supportUnderBarrel: false };
    case 'office-keyboard':
      return { main: { x: -0.04, y: 0 }, support: { x: 0.25, y: 0 }, supportUnderBarrel: false };
    case 'office-mug':
      return { main: { x: -0.08, y: 0 }, support: { x: 0.12, y: -0.04 }, supportUnderBarrel: false };
  }
  const launcher = ['rpg', 'grenade-launcher', 'snake-launcher', 'flying-snake', 'snake-grenade', 'bouncer', 'lava-spike-ball'].includes(kind);
  const minigun = kind.includes('minigun');
  const blade = kind === 'sword' || kind === 'blink-dagger';
  return {
    main: kind === 'spear' ? { x: 0, y: 0 } : blade ? { x: -0.05, y: -0.115 }
      : launcher ? { x: 0.015, y: -0.13 } : minigun ? { x: -0.02, y: -0.15 } : { x: -0.02, y: -0.12 },
    support: blade ? { x: -0.065, y: -0.175 } : { x: Math.min(0.23, length * 0.3), y: kind === 'spear' ? 0 : minigun ? -0.13 : launcher ? -0.08 : -0.065 },
    supportUnderBarrel: !blade && kind !== 'spear',
  };
}

/** The palm sits behind the weapon; these curled fingers cover a small part of its grip. */
export function gripFingers(contact: Vec2, anchor: WeaponAnchor, support = false) {
  const cap = (ax: number, ay: number, bx: number, by: number, r: number): Primitive => {
    const a = weaponPoint({ x: contact.x + ax, y: contact.y + ay }, anchor);
    const b = weaponPoint({ x: contact.x + bx, y: contact.y + by }, anchor);
    return { kind: PRIM_CAPSULE, ax: a.x, ay: a.y, bx: b.x, by: b.y, r };
  };
  const y = support ? 0.045 : 0;
  return {
    fingers: [cap(-0.045, y + 0.027, 0.047, y + 0.027, 0.035), cap(-0.047, y - 0.025, 0.035, y - 0.025, 0.035)],
    crease: cap(-0.032, y + 0.002, 0.025, y + 0.002, 0.012),
  };
}
